/**
 * Build VNode[] representing the `<defs>` children for a `CompileResult`.
 *
 * Emission order (must match pre-refactor `src/svg-generator.ts`):
 *   masks → clipPaths → gradients → patterns → markers
 *
 * Each branch preserves the attribute order of the pre-refactor emitter so
 * Phase 2's CLI byte-snapshots pass unchanged.
 */

import { resolveConicPlacement, type ConicViewport } from '../conic-param';
import { renderConic } from '../conic-renderer';
import { validateCSSIdent } from '../evaluator/sanitize';
import type {
  CompileResult,
  ElevationShadowFilterOutput,
  EmbossFilterOutput,
  FilterOutput,
  GlowFilterOutput,
  GradientOutput,
  InnerShadowFilterOutput,
  MarkerOutput,
  MaskOutput,
  MotionBlurFilterOutput,
  NoiseFilterOutput,
  PatternOutput,
  PixelateFilterOutput,
} from '../evaluator/types';
import type { ClipPathOutput } from '../evaluator/types';

import { h, type VNode } from './types';

/**
 * Defense-in-depth check on URLs that flow into <image href=...> from gpu
 * gradient rasterization. Producers (workspace-view, gradient-service) emit
 * data: URLs only; this assertion prevents a regression there from leaking a
 * remote URL into the SVG output.
 */
function assertSafeGpuImageUrl(url: string): void {
  if (!url.startsWith('data:image/')) {
    throw new Error(
      `GPU gradient href must be a data:image/... URI, got ${JSON.stringify(url.slice(0, 80))}`,
    );
  }
}

export interface BuildDefsOptions {
  /**
   * The viewBox origin (min-x / min-y). A conic gradient's pattern tile, its
   * inner-fill mask and its default centre are laid out from here, so a viewBox
   * such as `-100 -100 200 200` is covered rather than tiled from (0, 0)
   * (ISSUE-027). Defaults to 0.
   */
  originX?: number;
  originY?: number;
  /** SVG canvas width — used for conic gradient wedge sizing. Defaults to 200. */
  width?: number;
  /** SVG canvas height — used for conic gradient wedge sizing. Defaults to 200. */
  height?: number;
  /**
   * Emit playground-specific `data-*-def` attributes on each defs element.
   * Used by the preview-pane cleanup selector to remove previous-compilation
   * defs without affecting the static grid pattern. Default false — CLI must
   * not emit these (would break byte identity).
   */
  emitPlaygroundDataAttrs?: boolean;
  /**
   * Render conic/mesh/freeform/topo gradients as `<pattern><image/></pattern>`
   * instead of the CLI fallback (wedge paths for conic, flat rect for others).
   * The `<image>` `href` comes from `gpuGradientUrls` when present; when
   * absent the `href` is emitted empty, and the caller is expected to run a
   * post-pass that computes it (e.g. Canvas 2D conic fallback in the
   * playground). Default false — CLI gets wedges/rects.
   */
  useImageGradients?: boolean;
  /**
   * Pre-rendered data URLs per gradient id (from `playground/gpu/...`). Only
   * consulted when `useImageGradients` is true. Ignored otherwise.
   */
  gpuGradientUrls?: Map<string, string>;
}

export function buildDefs(result: CompileResult, options: BuildDefsOptions = {}): VNode[] {
  const view: ConicViewport = {
    x: options.originX ?? 0,
    y: options.originY ?? 0,
    width: options.width ?? 200,
    height: options.height ?? 200,
  };
  const emitData = options.emitPlaygroundDataAttrs ?? false;
  const useImg = options.useImageGradients ?? false;
  const urls = options.gpuGradientUrls;

  const defs: VNode[] = [];

  for (const mask of result.masks) {
    defs.push(buildMask(mask, emitData));
  }
  for (const clip of result.clipPaths) {
    defs.push(buildClipPath(clip, emitData));
  }
  for (const grad of result.gradients) {
    defs.push(...buildGradientDefs(grad, view, emitData, useImg, urls));
  }
  for (const pat of result.patterns ?? []) {
    defs.push(buildPattern(pat, emitData));
  }
  for (const marker of result.markers ?? []) {
    defs.push(buildMarker(marker, emitData));
  }
  for (const filter of result.filters ?? []) {
    defs.push(...buildFilterDefs(filter, emitData));
  }

  return defs;
}

/**
 * A filter normally emits a single `<filter>` element. Progressive motion blur
 * additionally needs a sibling `<linearGradient>` + mask `<rect>` (referenced by
 * its `feImage`), so it returns those defs ahead of the `<filter>`.
 */
function buildFilterDefs(filter: FilterOutput, emitData: boolean): VNode[] {
  if (filter.kind === 'motion-blur' && filter.motionType === 'progressive') {
    return [...buildProgressiveMaskDefs(filter, emitData), buildFilter(filter, emitData)];
  }
  return [buildFilter(filter, emitData)];
}

function buildMask(mask: MaskOutput, emitData: boolean): VNode {
  const attrs: Record<string, string> = { id: mask.id };
  if (emitData) attrs['data-mask-def'] = mask.id;
  const children: VNode[] = mask.elements.map((el) =>
    h('path', { d: el.pathData, ...el.styles }),
  );
  return h('mask', attrs, children);
}

function buildClipPath(clip: ClipPathOutput, emitData: boolean): VNode {
  const attrs: Record<string, string> = { id: clip.id };
  if (emitData) attrs['data-clippath-def'] = clip.id;
  const children: VNode[] = clip.elements.map((el) => h('path', { d: el.pathData }));
  return h('clipPath', attrs, children);
}

/**
 * A gradient normally emits one def. A conic gradient rendered as wedges with
 * a blended `innerFill` also needs a sibling `<radialGradient>` (and, for
 * `'transparent-blend'`, a `<mask>`) that its pattern content references, so
 * the conic branch may return several nodes; they precede the `<pattern>`.
 */
function buildGradientDefs(
  grad: GradientOutput,
  view: ConicViewport,
  emitData: boolean,
  useImageGradients: boolean,
  gpuGradientUrls: Map<string, string> | undefined,
): VNode[] {
  if (grad.type === 'conic' && !useImageGradients) {
    return buildConicWedgeDefs(grad, view);
  }
  return [buildGradient(grad, view, emitData, useImageGradients, gpuGradientUrls)];
}

/**
 * Wedge-path rendering of a conic gradient (CLI, VS Code preview, library):
 * one `<path>` per ~1° slice inside a `<pattern>`, following the same rules as
 * the playground's WebGPU shader (see `src/conic-param.ts`).
 */
function buildConicWedgeDefs(grad: GradientOutput, view: ConicViewport): VNode[] {
  // Pattern content is drawn in the tile's own coordinates (top-left = (0, 0)),
  // so the wedges, the inner-fill disc and the mask are tile-local; only the
  // <pattern> itself is placed at the viewBox origin (ISSUE-027).
  const placement = resolveConicPlacement(grad, view);
  const render = renderConic({
    cx: placement.cx,
    cy: placement.cy,
    viewWidth: placement.viewWidth,
    viewHeight: placement.viewHeight,
    from: grad.from ?? 0,
    to: grad.to ?? (grad.from ?? 0) + 2 * Math.PI,
    direction: grad.direction ?? 'cw',
    spread: grad.spread ?? 'clamp',
    stops: grad.stopsWithOklch ?? grad.stops,
    innerRadius: grad.innerRadius ?? 0,
    innerFill: grad.innerFill,
  });
  const cx = String(placement.cx);
  const cy = String(placement.cy);
  const size = { width: String(view.width), height: String(view.height) };
  const local = { x: '0', y: '0', ...size };
  const tile = { x: String(placement.tileX), y: String(placement.tileY), ...size };
  const siblings: VNode[] = [];
  let content: VNode[] = render.wedges.map((w) => h('path', { d: w.d, fill: w.fill }));

  if (render.innerOverlay) {
    const overlayId = `${grad.id}-inner-fill`;
    siblings.push(
      h(
        'radialGradient',
        { id: overlayId, gradientUnits: 'userSpaceOnUse', cx, cy, r: String(render.innerOverlay.radius) },
        render.innerOverlay.stops.map((s) =>
          h('stop', {
            offset: String(s.offset),
            'stop-color': render.innerOverlay!.color,
            'stop-opacity': String(Number(s.opacity.toFixed(4))),
          }),
        ),
      ),
    );
    content.push(h('circle', { cx, cy, r: String(render.innerOverlay.radius), fill: `url(#${overlayId})` }));
  }

  if (render.innerMask) {
    const maskGradId = `${grad.id}-inner-mask-ramp`;
    const maskId = `${grad.id}-inner-mask`;
    siblings.push(
      h(
        'radialGradient',
        { id: maskGradId, gradientUnits: 'userSpaceOnUse', cx, cy, r: String(render.innerMask.radius) },
        render.innerMask.stops.map((s) => {
          const level = Math.round(Number(s.luminance.toFixed(4)) * 255);
          return h('stop', { offset: String(s.offset), 'stop-color': `rgb(${level}, ${level}, ${level})` });
        }),
      ),
      h('mask', { id: maskId, maskUnits: 'userSpaceOnUse', ...local }, [
        h('rect', { ...local, fill: `url(#${maskGradId})` }),
      ]),
    );
    content = [h('g', { mask: `url(#${maskId})` }, content)];
  }

  const pattern = h('pattern', { id: grad.id, ...tile, patternUnits: 'userSpaceOnUse' }, content);
  return [...siblings, pattern];
}

function buildGradient(
  grad: GradientOutput,
  view: ConicViewport,
  emitData: boolean,
  useImageGradients: boolean,
  gpuGradientUrls: Map<string, string> | undefined,
): VNode {
  // Conic: pattern+image (playground). buildGradientDefs routes the wedge
  // form to buildConicWedgeDefs before this is reached, because that form can
  // emit sibling defs. The raster covers the viewBox, so the tile starts at
  // its origin (ISSUE-027); the <image> fills the tile.
  if (grad.type === 'conic') {
    {
      const placement = resolveConicPlacement(grad, view);
      const attrs: Record<string, string> = { id: grad.id };
      if (emitData) attrs['data-gradient-def'] = grad.id;
      attrs.x = String(placement.tileX);
      attrs.y = String(placement.tileY);
      attrs.width = String(view.width);
      attrs.height = String(view.height);
      attrs.patternUnits = 'userSpaceOnUse';
      const imgAttrs: Record<string, string> = {
        width: String(view.width),
        height: String(view.height),
      };
      const url = gpuGradientUrls?.get(grad.id);
      if (url) {
        assertSafeGpuImageUrl(url);
        imgAttrs.href = url;
      }
      return h('pattern', attrs, [h('image', imgAttrs)]);
    }
  }

  // Mesh / Freeform / Topo: either CLI fallback rect or playground pattern+image.
  if (grad.type === 'mesh' || grad.type === 'freeform' || grad.type === 'topo') {
    if (useImageGradients) {
      const attrs: Record<string, string> = { id: grad.id };
      if (emitData) attrs['data-gradient-def'] = grad.id;
      attrs.x = '0';
      attrs.y = '0';
      attrs.width = '1';
      attrs.height = '1';
      attrs.patternUnits = 'objectBoundingBox';
      attrs.patternContentUnits = 'objectBoundingBox';
      const imgAttrs: Record<string, string> = {
        width: '1',
        height: '1',
        preserveAspectRatio: 'none',
      };
      const url = gpuGradientUrls?.get(grad.id);
      if (url) {
        assertSafeGpuImageUrl(url);
        imgAttrs.href = url;
      }
      return h('pattern', attrs, [h('image', imgAttrs)]);
    }
    let w: number, hgt: number;
    if (grad.type === 'mesh') {
      w = grad.meshWidth ?? 200;
      hgt = grad.meshHeight ?? 200;
    } else if (grad.type === 'freeform') {
      w = grad.freeformWidth ?? 200;
      hgt = grad.freeformHeight ?? 200;
    } else {
      w = grad.topoWidth ?? 200;
      hgt = grad.topoHeight ?? 200;
    }
    let avgColor = '#808080';
    if (grad.type === 'topo') {
      if (grad.topoBaseColor) {
        avgColor = grad.topoBaseColor;
      } else {
        const contours = grad.topoContours ?? [];
        if (contours.length > 0) avgColor = contours[0].color;
      }
    } else {
      const points =
        grad.type === 'mesh' ? (grad.meshGrid ?? []).flat() : grad.freeformPoints ?? [];
      if (points.length > 0) avgColor = points[0].color;
    }
    // The placeholder tile starts at the viewBox origin, like the conic tile
    // (ISSUE-027): visually inert for a flat colour, but one rule for every
    // gradient that covers the canvas.
    return h(
      'pattern',
      {
        id: grad.id,
        x: String(view.x),
        y: String(view.y),
        width: String(w),
        height: String(hgt),
        patternUnits: 'userSpaceOnUse',
      },
      [h('rect', { width: String(w), height: String(hgt), fill: avgColor })],
    );
  }

  // Linear / Radial: proper <linearGradient> / <radialGradient>.
  const tagName = grad.type === 'linear' ? 'linearGradient' : 'radialGradient';
  const attrs: Record<string, string> = { id: grad.id };
  if (emitData) attrs['data-gradient-def'] = grad.id;
  for (const [key, value] of Object.entries(grad.attrs)) {
    attrs[key] = value;
  }
  if (grad.spreadMethod) attrs.spreadMethod = grad.spreadMethod;
  if (grad.gradientUnits) attrs.gradientUnits = grad.gradientUnits;
  if (grad.gradientTransform) attrs.gradientTransform = grad.gradientTransform;
  if (grad.colorInterpolation) attrs['color-interpolation'] = grad.colorInterpolation;
  if (grad.href) {
    // Defense-in-depth: gradient inheritance refs are validated at evaluation
    // (LinearGradient/RadialGradient constructors); re-check here to protect
    // any future caller that bypasses the constructor path.
    validateCSSIdent(grad.href, 'gradient-href');
    attrs.href = `#${grad.href}`;
  }

  const stops: VNode[] = grad.stops.map((s) =>
    h('stop', { offset: String(s.offset), 'stop-color': s.color }),
  );
  return h(tagName, attrs, stops);
}

function buildPattern(pat: PatternOutput, emitData: boolean): VNode {
  const attrs: Record<string, string> = { id: pat.id };
  if (emitData) attrs['data-pattern-def'] = pat.id;
  attrs.x = String(pat.x);
  attrs.y = String(pat.y);
  attrs.width = String(pat.width);
  attrs.height = String(pat.height);
  if (pat.patternUnits) attrs.patternUnits = pat.patternUnits;
  if (pat.patternTransform) attrs.patternTransform = pat.patternTransform;
  if (pat.patternContentUnits) attrs.patternContentUnits = pat.patternContentUnits;
  const children: VNode[] = pat.elements.map((el) =>
    h('path', { d: el.pathData, ...el.styles }),
  );
  return h('pattern', attrs, children);
}

function buildMarker(marker: MarkerOutput, emitData: boolean): VNode {
  const attrs: Record<string, string> = { id: marker.id };
  if (emitData) attrs['data-marker-def'] = marker.id;
  attrs.viewBox = marker.viewBox;
  attrs.markerWidth = String(marker.markerWidth);
  attrs.markerHeight = String(marker.markerHeight);
  attrs.refX = marker.refX;
  attrs.refY = marker.refY;
  if (marker.markerUnits) attrs.markerUnits = marker.markerUnits;
  if (marker.orient) attrs.orient = marker.orient;
  if (marker.preserveAspectRatio) attrs.preserveAspectRatio = marker.preserveAspectRatio;
  const children: VNode[] = marker.elements.map((el) =>
    h('path', { d: el.pathData, ...el.styles }),
  );
  return h('marker', attrs, children);
}

// ---------------------------------------------------------------------------
// Custom filter recipes
// ---------------------------------------------------------------------------
//
// A filter wraps a primitive chain inside <filter id="...">. Dispatch is by
// `kind`; additional filter kinds can slot in next to `buildNoisePrimitives`
// without changes to wrapper attrs or dispatch shape.
//
// All recipes use a -10%..+120% region so grain extends slightly beyond strokes
// and dropshadow-style spread fits without clipping (matches the convention
// other defs producers use).

/** Filter region rectangle. Each filter kind picks its own based on how far primitives extend. */
interface FilterRegion {
  x: string;
  y: string;
  width: string;
  height: string;
}

function filterRegion(filter: FilterOutput): FilterRegion {
  switch (filter.kind) {
    case 'noise':
    case 'emboss':
    case 'inner-shadow':
      return { x: '-10%', y: '-10%', width: '120%', height: '120%' };
    case 'glow':
      // Outer glow extends beyond the painted bounds by ~radius + spread; give it a generous region.
      return { x: '-50%', y: '-50%', width: '200%', height: '200%' };
    case 'elevation-shadow':
      // Soft layer offset + blur can reach ~elevation*6 outside the source.
      return { x: '-100%', y: '-100%', width: '300%', height: '300%' };
    case 'pixelate':
      // Tile + dilate operate inside the source bounds.
      return { x: '0%', y: '0%', width: '100%', height: '100%' };
    case 'motion-blur':
      // Linear taps reach ±distance/2 — a generous region avoids clipping the
      // smear. Progressive maps a 0→1 gradient ramp across the filter region, so
      // it needs a TIGHT region (≈the bbox); an oversized one compresses the
      // ramp to the middle band and the effect washes out to a uniform haze.
      return filter.motionType === 'progressive'
        ? { x: '-10%', y: '-10%', width: '120%', height: '120%' }
        : { x: '-50%', y: '-50%', width: '200%', height: '200%' };
    default: {
      const _exhaustive: never = filter;
      throw new Error(`Unsupported filter kind in filterRegion: ${String((_exhaustive as { kind: string }).kind)}`);
    }
  }
}

function buildFilter(filter: FilterOutput, emitData: boolean): VNode {
  const attrs: Record<string, string> = { id: filter.id };
  if (emitData) attrs['data-filter-def'] = filter.id;
  const region = filterRegion(filter);
  attrs.x = region.x;
  attrs.y = region.y;
  attrs.width = region.width;
  attrs.height = region.height;
  // PixelateFilter relies on user-space primitive coordinates (feFlood at
  // pixel offsets like 3,3 with width=12 etc.). The default filterUnits is
  // objectBoundingBox, which scales those coords to 0..1 of the source bbox
  // and produces an empty result. userSpaceOnUse keeps the primitive coords
  // as literal pixel values.
  if (filter.kind === 'pixelate') {
    attrs.filterUnits = 'userSpaceOnUse';
  }
  let children: VNode[];
  switch (filter.kind) {
    case 'noise':
      children = buildNoisePrimitives(filter);
      break;
    case 'glow':
      children = buildGlowPrimitives(filter);
      break;
    case 'emboss':
      children = buildEmbossPrimitives(filter);
      break;
    case 'elevation-shadow':
      children = buildElevationShadowPrimitives(filter);
      break;
    case 'inner-shadow':
      children = buildInnerShadowPrimitives(filter);
      break;
    case 'pixelate':
      children = buildPixelatePrimitives(filter);
      break;
    case 'motion-blur':
      children = buildMotionBlurPrimitives(filter);
      break;
    default: {
      const _exhaustive: never = filter;
      throw new Error(`Unsupported filter kind: ${String((_exhaustive as { kind: string }).kind)}`);
    }
  }
  return h('filter', attrs, children);
}

function turbulenceAttrs(filter: NoiseFilterOutput): Record<string, string> {
  const attrs: Record<string, string> = {
    type: filter.style === 'speckle' ? 'turbulence' : 'fractalNoise',
    baseFrequency: String(filter.scale),
    numOctaves: String(filter.octaves),
    seed: String(filter.seed),
    result: 'turb',
  };
  if (filter.stitch) attrs.stitchTiles = 'stitch';
  return attrs;
}

/** Linear alpha ramp via feComponentTransfer — modulates noise intensity uniformly across presets. */
function amountTransferNode(amount: number, inResult: string, outResult: string): VNode {
  return h('feComponentTransfer', { in: inResult, result: outResult }, [
    h('feFuncA', { type: 'linear', slope: String(amount) }),
  ]);
}

/** Symmetric contrast pump on RGB channels — used by the Gradient style and any preset with contrast > 1. */
function contrastPumpNode(contrast: number, inResult: string, outResult: string): VNode {
  // y = clamp(contrast * (x - 0.5) + 0.5, 0..1) — implemented as linear with intercept.
  const slope = String(contrast);
  const intercept = String(0.5 - 0.5 * contrast);
  return h('feComponentTransfer', { in: inResult, result: outResult }, [
    h('feFuncR', { type: 'linear', slope, intercept }),
    h('feFuncG', { type: 'linear', slope, intercept }),
    h('feFuncB', { type: 'linear', slope, intercept }),
  ]);
}

/**
 * All current noise presets share the same primitive chain shape:
 *   turbulence → optional contrast pump → composite-in SourceAlpha →
 *   optional luminance-to-alpha (monochrome) → amount transfer → blend.
 *
 * Per-preset visual character flows entirely through:
 *   - `turbulenceAttrs(filter)`: Speckle picks `type: 'turbulence'`; others
 *     `'fractalNoise'`. `stitchTiles` is set when `filter.stitch === true`.
 *   - Default parameter values baked in at construction (scale/octaves/blend
 *     /contrast/monochrome — see `noiseFilterDefaults` in the evaluator).
 *
 * Keeping a single chain function means every user property — `contrast`,
 * `monochrome`, `amount`, `blend` — affects every preset uniformly. The
 * earlier per-preset builders silently dropped `contrast` on four of five
 * styles and forced `monochrome` on Static.
 */
function buildNoisePrimitives(filter: NoiseFilterOutput): VNode[] {
  const nodes: VNode[] = [h('feTurbulence', turbulenceAttrs(filter))];
  let last = 'turb';
  if (filter.contrast !== 1) {
    nodes.push(contrastPumpNode(filter.contrast, last, 'pumped'));
    last = 'pumped';
  }
  nodes.push(h('feComposite', { in: last, in2: 'SourceAlpha', operator: 'in', result: 'masked' }));
  last = 'masked';
  if (filter.monochrome) {
    nodes.push(h('feColorMatrix', { in: last, type: 'luminanceToAlpha', result: 'mono' }));
    last = 'mono';
  }
  nodes.push(amountTransferNode(filter.amount, last, 'noise'));
  nodes.push(h('feBlend', { in: 'SourceGraphic', in2: 'noise', mode: filter.blend }));
  return nodes;
}

// ---------------------------------------------------------------------------
// GlowFilter — outer or inner soft glow.
// ---------------------------------------------------------------------------

function buildGlowPrimitives(filter: GlowFilterOutput): VNode[] {
  const nodes: VNode[] = [];
  let blurIn = 'SourceAlpha';
  if (filter.spread > 0) {
    nodes.push(
      h('feMorphology', {
        in: 'SourceAlpha',
        operator: filter.mode === 'inner' ? 'erode' : 'dilate',
        radius: String(filter.spread),
        result: 'shaped',
      }),
    );
    blurIn = 'shaped';
  }
  nodes.push(h('feGaussianBlur', { in: blurIn, stdDeviation: String(filter.radius), result: 'blur' }));
  if (filter.mode === 'outer') {
    nodes.push(h('feFlood', { 'flood-color': filter.color, 'flood-opacity': String(filter.opacity), result: 'flood' }));
    nodes.push(h('feComposite', { in: 'flood', in2: 'blur', operator: 'in', result: 'coloredGlow' }));
    nodes.push(
      h('feMerge', {}, [
        h('feMergeNode', { in: 'coloredGlow' }),
        h('feMergeNode', { in: 'SourceGraphic' }),
      ]),
    );
  } else {
    // Inner: invert the blurred shape against the source alpha so glow rides the inside edge.
    nodes.push(h('feComposite', { in: 'SourceAlpha', in2: 'blur', operator: 'out', result: 'inverted' }));
    nodes.push(h('feFlood', { 'flood-color': filter.color, 'flood-opacity': String(filter.opacity), result: 'flood' }));
    nodes.push(h('feComposite', { in: 'flood', in2: 'inverted', operator: 'in', result: 'innerGlow' }));
    // Clip glow to source so it never leaks outside.
    nodes.push(h('feComposite', { in: 'innerGlow', in2: 'SourceAlpha', operator: 'in', result: 'clippedGlow' }));
    nodes.push(
      h('feMerge', {}, [
        h('feMergeNode', { in: 'SourceGraphic' }),
        h('feMergeNode', { in: 'clippedGlow' }),
      ]),
    );
  }
  return nodes;
}

// ---------------------------------------------------------------------------
// EmbossFilter — feSpecularLighting + feDistantLight.
// ---------------------------------------------------------------------------

function radToDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

function buildEmbossPrimitives(filter: EmbossFilterOutput): VNode[] {
  const nodes: VNode[] = [];
  let specIn = 'SourceAlpha';
  if (filter.smooth > 0) {
    nodes.push(h('feGaussianBlur', { in: 'SourceAlpha', stdDeviation: String(filter.smooth), result: 'blur' }));
    specIn = 'blur';
  }
  nodes.push(
    h(
      'feSpecularLighting',
      {
        in: specIn,
        surfaceScale: String(filter.depth),
        specularConstant: String(filter.strength),
        specularExponent: String(filter.shininess),
        'lighting-color': filter.lightColor,
        result: 'spec',
      },
      [
        h('feDistantLight', {
          azimuth: cleanNum(radToDeg(filter.angle)),
          elevation: cleanNum(radToDeg(filter.elevation)),
        }),
      ],
    ),
  );
  nodes.push(h('feComposite', { in: 'spec', in2: 'SourceAlpha', operator: 'in', result: 'masked' }));
  nodes.push(
    h('feComposite', {
      in: 'SourceGraphic',
      in2: 'masked',
      operator: 'arithmetic',
      k1: '0',
      k2: '1',
      k3: '1',
      k4: '0',
    }),
  );
  return nodes;
}

// ---------------------------------------------------------------------------
// ElevationShadowFilter — three layered soft shadows.
// ---------------------------------------------------------------------------

interface ShadowLayer {
  distanceRatio: number;
  blurRatio: number;
  opacity: number;
}

const ELEVATION_LAYERS: ShadowLayer[] = [
  { distanceRatio: 0.3, blurRatio: 0.5, opacity: 0.3 },
  { distanceRatio: 0.6, blurRatio: 1.0, opacity: 0.18 },
  { distanceRatio: 1.0, blurRatio: 2.0, opacity: 0.12 },
];

/**
 * Round near-zero floats to 0 and trim long decimal tails so the emitted SVG
 * is readable. Without this, `cos(PI/2) * 6` shows up as `3.67e-16` and
 * `0.3 * 6` as `1.7999999999999998`. Six decimal places is plenty of
 * resolution for filter-region geometry.
 */
function cleanNum(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  if (Math.abs(n) < 1e-9) return '0';
  const rounded = Math.round(n * 1e6) / 1e6;
  return String(rounded);
}

function buildSoftShadowLayer(
  index: number,
  dx: number,
  dy: number,
  blur: number,
  color: string,
  opacity: number,
): VNode[] {
  const i = index + 1;
  return [
    h('feGaussianBlur', { in: 'SourceAlpha', stdDeviation: cleanNum(blur), result: `b${i}` }),
    h('feOffset', { in: `b${i}`, dx: cleanNum(dx), dy: cleanNum(dy), result: `o${i}` }),
    h('feFlood', { 'flood-color': color, 'flood-opacity': cleanNum(opacity), result: `f${i}` }),
    h('feComposite', { in: `f${i}`, in2: `o${i}`, operator: 'in', result: `s${i}` }),
  ];
}

function buildElevationShadowPrimitives(filter: ElevationShadowFilterOutput): VNode[] {
  const nodes: VNode[] = [];
  if (filter.elevation <= 0) {
    // Flat — emit a pass-through chain (Identity on SourceGraphic). feMerge with just SourceGraphic.
    nodes.push(h('feMerge', {}, [h('feMergeNode', { in: 'SourceGraphic' })]));
    return nodes;
  }
  const cosD = Math.cos(filter.direction);
  const sinD = Math.sin(filter.direction);
  const mergeChildren: VNode[] = [];
  for (let i = 0; i < ELEVATION_LAYERS.length; i++) {
    const layer = ELEVATION_LAYERS[i];
    const distance = filter.elevation * layer.distanceRatio * filter.tightness;
    const blur = filter.elevation * layer.blurRatio * filter.tightness;
    const dx = distance * cosD;
    const dy = distance * sinD;
    const sub = buildSoftShadowLayer(i, dx, dy, blur, filter.color, layer.opacity);
    nodes.push(...sub);
    mergeChildren.push(h('feMergeNode', { in: `s${i + 1}` }));
  }
  mergeChildren.push(h('feMergeNode', { in: 'SourceGraphic' }));
  nodes.push(h('feMerge', {}, mergeChildren));
  return nodes;
}

// ---------------------------------------------------------------------------
// InnerShadowFilter — inset shadow clipped to source.
// ---------------------------------------------------------------------------

function buildInnerShadowPrimitives(filter: InnerShadowFilterOutput): VNode[] {
  return [
    h('feGaussianBlur', { in: 'SourceAlpha', stdDeviation: String(filter.blur), result: 'blur' }),
    h('feOffset', { in: 'blur', dx: String(filter.offsetX), dy: String(filter.offsetY), result: 'offset' }),
    h('feComposite', { in: 'SourceAlpha', in2: 'offset', operator: 'out', result: 'inverted' }),
    h('feFlood', { 'flood-color': filter.color, 'flood-opacity': String(filter.opacity), result: 'flood' }),
    h('feComposite', { in: 'flood', in2: 'inverted', operator: 'in', result: 'innerShadow' }),
    h('feComposite', { in: 'innerShadow', in2: 'SourceAlpha', operator: 'in', result: 'clipped' }),
    h('feMerge', {}, [h('feMergeNode', { in: 'SourceGraphic' }), h('feMergeNode', { in: 'clipped' })]),
  ];
}

// ---------------------------------------------------------------------------
// PixelateFilter — flood-tile-dilate mosaic.
// ---------------------------------------------------------------------------

function buildPixelatePrimitives(filter: PixelateFilterOutput): VNode[] {
  const offset = filter.radius / 2;
  return [
    h('feFlood', {
      x: String(offset),
      y: String(offset),
      width: '2',
      height: '2',
      'flood-color': '#000',
    }),
    h('feComposite', { width: String(filter.width), height: String(filter.height) }),
    h('feTile', { result: 'a' }),
    h('feComposite', { in: 'SourceGraphic', in2: 'a', operator: 'in' }),
    h('feMorphology', { operator: 'dilate', radius: String(filter.radius) }),
  ];
}

// ---------------------------------------------------------------------------
// MotionBlurFilter — directional (linear) and progressive blur.
// ---------------------------------------------------------------------------

function buildMotionBlurPrimitives(filter: MotionBlurFilterOutput): VNode[] {
  return filter.motionType === 'progressive'
    ? buildProgressiveMotionBlurPrimitives(filter)
    : buildLinearMotionBlurPrimitives(filter);
}

/**
 * Linear (directional) blur. Physical motion blur is the integral of the source
 * translated along the motion vector over the shutter interval; we approximate
 * it with `samples` copies offset evenly along `angle`, centered on the source
 * (t ∈ [-0.5, +0.5] so the smear is symmetric, not a one-sided ghost). Each tap
 * is pre-scaled to 1/N alpha (feComponentTransfer) and the taps are *summed*
 * additively (feComposite operator="arithmetic", k2=k3=1) — NOT feMerge, whose
 * over-compositing would bias toward the last tap and brighten the result.
 *
 * Discrete offset copies of a hard-edged shape read as ghost repeats rather than
 * a smooth smear, so a final feGaussianBlur sized to the tap spacing fuses the
 * copies into a continuous blur. The smoothing scales with spacing: dense taps
 * (already smooth) get a negligible blur, sparse taps get more.
 */
function buildLinearMotionBlurPrimitives(filter: MotionBlurFilterOutput): VNode[] {
  const n = Math.max(2, Math.round(filter.samples));
  const cos = Math.cos(filter.angle);
  const sin = Math.sin(filter.angle);
  const slope = cleanNum(1 / n);
  const nodes: VNode[] = [];
  let acc = '';
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1) - 0.5;
    const dx = filter.distance * t * cos;
    const dy = filter.distance * t * sin;
    const offRes = `t${i}o`;
    nodes.push(h('feOffset', { in: 'SourceGraphic', dx: cleanNum(dx), dy: cleanNum(dy), result: offRes }));
    const scaledRes = i === 0 ? 'acc0' : `t${i}s`;
    nodes.push(
      h('feComponentTransfer', { in: offRes, result: scaledRes }, [
        h('feFuncA', { type: 'linear', slope }),
      ]),
    );
    if (i === 0) {
      acc = 'acc0';
    } else {
      const next = `acc${i}`;
      nodes.push(
        h('feComposite', {
          in: acc,
          in2: scaledRes,
          operator: 'arithmetic',
          k1: '0',
          k2: '1',
          k3: '1',
          k4: '0',
          result: next,
        }),
      );
      acc = next;
    }
  }
  // Fuse the discrete taps into a smooth smear. The ghost copies are spaced
  // ALONG the motion axis, so the smoothing must be directional: a two-value
  // stdDeviation aligned to (cos, sin) blurs along the smear (merging copies)
  // while leaving the perpendicular edge — the object's cross-section — crisp.
  // An isotropic blur would dull that edge and read as a soft blob instead of
  // motion. Sized to ~1.5× the tap spacing and capped for long smears.
  const spacing = Math.abs(filter.distance) / (n - 1);
  const smoothing = Math.min(6, spacing * 1.5);
  if (smoothing > 0.05) {
    const sx = cleanNum(smoothing * Math.abs(cos));
    const sy = cleanNum(smoothing * Math.abs(sin));
    nodes.push(h('feGaussianBlur', { in: acc, stdDeviation: `${sx} ${sy}` }));
  }
  return nodes;
}

/**
 * Progressive blur. A single blurred copy is gated by an `feImage` of a
 * gradient mask (the sibling defs from `buildProgressiveMaskDefs`). Because the
 * filter region stays in the default `objectBoundingBox` units and the mask
 * rect fills 100% of that region, the ramp tracks the *element's own bounds* —
 * sharp at the near edge, fully blurred at the far edge along `angle`.
 */
function buildProgressiveMotionBlurPrimitives(filter: MotionBlurFilterOutput): VNode[] {
  // Crossfade sharp↔blurred along the ramp. Masking the *sharp* source by the
  // inverted ramp (and the blurred copy by the ramp) means the near edge shows
  // only the sharp source and the far edge shows only the blur — a genuine
  // progressive blur, not a sharp image with a blurred halo painted on top.
  return [
    h('feGaussianBlur', { in: 'SourceGraphic', stdDeviation: cleanNum(filter.distance), result: 'blurred' }),
    h('feImage', { href: `#${filter.id}-mask`, result: 'ramp' }),
    h('feComponentTransfer', { in: 'ramp', result: 'invRamp' }, [
      h('feFuncA', { type: 'table', tableValues: '1 0' }),
    ]),
    h('feComposite', { in: 'SourceGraphic', in2: 'invRamp', operator: 'in', result: 'sharpPart' }),
    h('feComposite', { in: 'blurred', in2: 'ramp', operator: 'in', result: 'blurPart' }),
    // Sum the two complementary halves additively (NOT feMerge). The masks are
    // exact complements (invRamp + ramp = 1), so summing yields alpha 1 across
    // the crossfade; feMerge's over-compositing would dip a solid shape to
    // ~0.75 alpha through the midband and lighten it — the same trap avoided in
    // the linear chain.
    h('feComposite', {
      in: 'sharpPart',
      in2: 'blurPart',
      operator: 'arithmetic',
      k1: '0',
      k2: '1',
      k3: '1',
      k4: '0',
    }),
  ];
}

/**
 * Sibling defs for progressive blur: a `<linearGradient>` (alpha 0→1 along
 * `angle`, objectBoundingBox) and the mask `<rect>` it fills. The `feImage` in
 * `buildProgressiveMotionBlurPrimitives` references the rect by id.
 */
function buildProgressiveMaskDefs(filter: MotionBlurFilterOutput, emitData: boolean): VNode[] {
  const cos = Math.cos(filter.angle);
  const sin = Math.sin(filter.angle);
  const gradId = `${filter.id}-ramp`;
  const maskId = `${filter.id}-mask`;
  const gradAttrs: Record<string, string> = {
    id: gradId,
    x1: cleanNum(0.5 - 0.5 * cos),
    y1: cleanNum(0.5 - 0.5 * sin),
    x2: cleanNum(0.5 + 0.5 * cos),
    y2: cleanNum(0.5 + 0.5 * sin),
    gradientUnits: 'objectBoundingBox',
  };
  if (emitData) gradAttrs['data-filter-def'] = filter.id;
  const grad = h('linearGradient', gradAttrs, [
    h('stop', { offset: '0', 'stop-color': '#000', 'stop-opacity': '0' }),
    h('stop', { offset: '1', 'stop-color': '#000', 'stop-opacity': '1' }),
  ]);
  const rectAttrs: Record<string, string> = {
    id: maskId,
    x: '0',
    y: '0',
    width: '100%',
    height: '100%',
    fill: `url(#${gradId})`,
  };
  if (emitData) rectAttrs['data-filter-def'] = filter.id;
  return [grad, h('rect', rectAttrs)];
}
